import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createOptionsFormatter,
    type OptionItem,
} from '@/components/ui/data-table-customized'
import ServiceContract from '@/modules/Service/components/Detail/Templates/shared/ServiceContract'
import { SERVICE_STATUS } from '@/modules/Service/constants'
import type { Service } from '@/modules/Service/types'
import { formatDate } from '@/utils/function'
import type { TFunction } from 'i18next'

export type OtherStandardPaymentRange = {
    start_date?: string
    end_date?: string
    price?: number
}

export type OtherStandardSchedule = {
    payment_method?: string
    payment_mode?: string
    description?: string
    ranges?: OtherStandardPaymentRange[]
}

export type OtherStandardRow = Service & {
    description?: string
    payment_ranges?: OtherStandardPaymentRange[]
}

const getStatusOptions = (t: TFunction): OptionItem[] =>
    SERVICE_STATUS(t).map((item) => ({
        label: item.label,
        value: item.value,
        color: item.color as OptionItem['color'],
    }))

const formatRange = (range: OtherStandardPaymentRange) => {
    const dateRange = [
        formatDate(range.start_date || null),
        formatDate(range.end_date || null),
    ]
        .filter(Boolean)
        .join(' - ')
    const price = range.price
        ? new Intl.NumberFormat('en-US').format(range.price)
        : '0'

    return [dateRange || '-', price].join(' | ')
}

export const createOtherStandardColumns = (
    t: TFunction
): DataTableColumn<OtherStandardRow>[] => [
    {
        key: 'service_name',
        title: t('service_name'),
        width: 220,
        pinned: 'left',
        render: (value) => value?.toString()?.toUpperCase() || '-',
    },
    {
        key: 'service_status',
        title: t('service_status'),
        width: 150,
        formatter: createOptionsFormatter({
            options: getStatusOptions(t),
        }),
    },
    {
        key: 'service_price_register',
        title: t('service_price_register'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'description',
        title: t('description', { defaultValue: 'Description' }),
        width: 260,
        render: (value) => value || '-',
    },
    {
        key: 'employee_names',
        title: t('employee_sale_tracking_ids'),
        width: 220,
        render: (value) => value?.join(', ') || '-',
    },
    {
        key: 'payment_ranges',
        title: t('other_standard.payment_ranges', {
            defaultValue: 'Payment periods',
        }),
        width: 360,
        render: (value) => {
            if (!Array.isArray(value) || value.length === 0) return '-'
            return (
                <div className="space-y-1">
                    {value.map((range, index) => (
                        <div key={index} className="text-xs leading-5">
                            {formatRange(range)}
                        </div>
                    ))}
                </div>
            )
        },
    },
    {
        key: 'service_contract',
        title: t('service_contract'),
        width: 300,
        render: (_, row) => {
            if (!row?.service_id) return null
            return <ServiceContract serviceID={row.service_id} />
        },
    },
]
